import React from "react";
import { Radio, Form, Space, RadioChangeEvent } from "antd";
import { useTranslation } from "react-i18next";
import { OptionType } from "@type/core/form/field.types";
import { InternalNamePath } from "rc-field-form/lib/interface";
import { formHelper } from "@utils/helpers/form-helper";
import FormFieldLabel from "@components/core/labels/FormFieldLabel";

interface PropsType {
  showLabel?: boolean;
  module: string;
  field: string | InternalNamePath;
  required?: boolean;
  emptyErrorMessage?: string;
  showTooltip?: boolean;
  labelTooltip?: string;
  readOnly?: boolean;
  labelClassName?: string;
  onChange?: (e: RadioChangeEvent) => void;
  className?: string;
  fieldPrefix?: string;
  disabled?: boolean;
  options: OptionType[];
  value?: string;
  direction?: "horizontal" | "vertical";
  type?: "default" | "button";
  optionClassName?: string;
}

const RadioFormField: React.FunctionComponent<PropsType> = (
  props: PropsType,
) => {
  const {
    showLabel,
    showTooltip,
    labelTooltip,
    module,
    field,
    required,
    emptyErrorMessage,
    readOnly,
    labelClassName,
    onChange,
    className = "",
    fieldPrefix = "",
    disabled,
    options,
    value,
    direction = "vertical",
    optionClassName = "",
    type = "default",
  } = props;
  const { t } = useTranslation();

  const isRequired: boolean = !!readOnly ? false : !!required;

  const i18nField = formHelper.getI18nFieldFromField(field);

  const key = `field_${String(field)}`.replace(",", "_");

  const inputLabel = t(`${module}.fields.${i18nField}.label`);

  return (
    <>
      <Form.Item
        name={`${fieldPrefix}${String(field)}`}
        key={key}
        label={
          readOnly || showLabel ? (
            <FormFieldLabel
              field={field}
              label={inputLabel}
              className={labelClassName}
              showTooltip={showTooltip}
              tooltipLabel={labelTooltip}
            />
          ) : (
            ""
          )
        }
        rules={
          isRequired
            ? [
                {
                  validator: (_, value) =>
                    value
                      ? Promise.resolve()
                      : Promise.reject(
                          formHelper.getErrorMessage(emptyErrorMessage),
                        ),
                },
              ]
            : []
        }
        className={className}
        valuePropName="checked"
      >
        <Radio.Group
          disabled={readOnly || disabled}
          className={readOnly ? " readonly-form-field" : ""}
          id={key}
          onChange={onChange}
          value={value}
          optionType={type}
          buttonStyle={type === "button" ? "solid" : "outline"}
        >
          <Space direction={direction}>
            {options.map((option, index) => (
              <Radio
                key={`option_${option.value}_${index}`}
                value={option.value}
                className={`${optionClassName} ${option.className ?? ""}`}
                data-test-type="radio-option"
                data-test={String(field)}
                data-alt={t(option.label)}
              >
                <span className={`${option.className ?? ""}`}>
                  {t(option.label)}
                </span>
              </Radio>
            ))}
          </Space>
        </Radio.Group>
      </Form.Item>
    </>
  );
};

export default RadioFormField;
